/**
 * SitePick front — 共有（site-share。.feature/site-share/spec.md §4.2 / §9.2）。
 *
 * 部品は parts/share-button.php の `<details class="c-share js-sp-share">`（JS なしでも開閉でき、
 * URL 欄・「LINEで送る」・「メール」はサーバ描画のリンク）。この JS がすること:
 *  - スマートフォン（pointer: coarse）で Web Share API が使えるときは、`<summary>` のクリックを横取りして
 *    OS の共有シートを開く（payload は `{ title, url }` だけ。`text` は渡さない = iOS の「コピー」が URL を落とす対策）
 *  - それ以外（PC・Web Share 不可）はパネル（リンクをコピー → LINEで送る → メール → PC で使えるときだけ その他のアプリ）
 *  - 計測は GA4 推奨イベント `share`（遷移 = line / email、完了 = web_share / copy）。キャンセル・失敗は送らない
 *
 * 共有 URL とタイトルは **必ず** サーバが描画した `data-sp-share-url` / `data-sp-share-title` から読む
 * （閲覧中のページのアドレスや文書のタイトルからは組まない = 文脈パラメータを受け手へ渡さない）。
 * 通信（fetch / XHR / beacon）はしない。クリップボードは S9 `copyText()` だけを使う。
 */

import { share as trackShare, readItem } from "./analytics.js";
import { copyText } from "./clipboard.js";
import { announce } from "./live-region.js";

/** GA4 `share` の語彙（measurement.yaml と同じ。これ以外の値は送らない）。 */
export const SHARE_METHODS = ["web_share", "copy", "line", "email"];
export const SHARE_TYPES = ["product", "demo"];
export const SHARE_LOCATIONS = ["detail_fv", "detail_foot", "live_foot", "live_float"];

/** コピー完了の表示時間（ms）。 */
const COPIED_MS = 1500;

function msg(key, fallback) {
  const m = (window.SITEPICK && window.SITEPICK.messages) || {};
  return typeof m[key] === "string" && m[key] ? m[key] : fallback;
}

/**
 * Web Share API で URL を共有できるか（secure context + navigator.share + canShare）。
 *
 * @param {string} url
 * @param {{ isSecureContext?: boolean }} [win]
 * @param {{ share?: Function, canShare?: Function }} [nav]
 * @returns {boolean}
 */
export function canWebShare(url, win = window, nav = navigator) {
  try {
    if (!url || !win || win.isSecureContext !== true || !nav || typeof nav.share !== "function") {
      return false;
    }
    if (typeof nav.canShare !== "function") {
      return true;
    }
    return nav.canShare({ url }) !== false;
  } catch {
    return false;
  }
}

/**
 * パネルを先に出す環境か（PC = hover できて精密なポインタ。SHARE-06=B）。UA では判定しない。
 *
 * @param {{ matchMedia?: Function }} [win]
 * @returns {boolean}
 */
export function prefersPanel(win = window) {
  try {
    return typeof win.matchMedia === "function" && win.matchMedia("(hover: hover) and (pointer: fine)").matches;
  } catch {
    return true;
  }
}

/**
 * 計測パラメータを組む（語彙外なら null = 送らない）。
 *
 * @param {string} method
 * @param {string} type
 * @param {string} place share_location の値
 * @param {Object|null} item `data-sp-item` の JSON
 * @returns {{ method: string, content_type: string, item_id: string, share_location: string }|null}
 */
export function shareParams(method, type, place, item) {
  if (!SHARE_METHODS.includes(method) || !SHARE_TYPES.includes(type) || !SHARE_LOCATIONS.includes(place)) {
    return null;
  }
  const itemId = item && item.item_id ? String(item.item_id) : "";
  return { method, content_type: type, item_id: itemId, share_location: place };
}

function track(el, method) {
  const params = shareParams(method, el.getAttribute("data-sp-share-type") || "", el.getAttribute("data-sp-share-location") || "", readItem(el));
  if (params) {
    trackShare(params);
  }
}

/** 開いている他の共有パネルを閉じる（同時に 2 つ開かない）。 */
function closeOthers(except) {
  document.querySelectorAll(".js-sp-share[open]").forEach((el) => {
    if (el !== except) {
      el.open = false;
    }
  });
}

/**
 * パネルを閉じる。フォーカスがパネル内にあったときだけ `<summary>` へ戻す
 * （外側のクリックで閉じたときに、押された要素からフォーカスを奪わない）。
 */
function closePanel(el) {
  const hadFocus = el.contains(document.activeElement);
  el.open = false;
  if (hadFocus) {
    const summary = el.querySelector("summary");
    if (summary) {
      summary.focus();
    }
  }
}

/**
 * OS の共有シートを開く。settle するまで is-busy（再入させない。タイムアウトは設けない）。
 *
 * @param {HTMLDetailsElement} el
 * @param {string} url
 * @param {string} title
 */
async function nativeShare(el, url, title) {
  if (el.classList.contains("is-busy")) {
    return;
  }
  el.classList.add("is-busy");
  el.setAttribute("aria-busy", "true");
  try {
    const data = { url };
    if (title) {
      data.title = title;
    }
    await navigator.share(data);
    track(el, "web_share");
  } catch (error) {
    const name = error && error.name;
    // AbortError = キャンセル（何もしない・計測しない）/ InvalidStateError = 前回が未完了（無視）。
    // それ以外（NotAllowedError / DataError / TypeError など）はパネルで代替する。
    if (name !== "AbortError" && name !== "InvalidStateError") {
      el.open = true;
    }
  } finally {
    el.classList.remove("is-busy");
    el.removeAttribute("aria-busy");
  }
}

/**
 * 1 つの共有部品を起動する。
 *
 * @param {HTMLDetailsElement} el
 * @param {AbortSignal} signal
 */
function setup(el, signal) {
  const url = el.getAttribute("data-sp-share-url") || "";
  const title = el.getAttribute("data-sp-share-title") || "";
  const summary = el.querySelector("summary");
  if (!url || !summary) {
    return;
  }
  const copyBtn = el.querySelector(".js-sp-share-copy");
  const nativeBtn = el.querySelector(".js-sp-share-native");
  const input = el.querySelector(".js-sp-share-input");
  const note = el.querySelector(".js-sp-share-note");
  const copyLabel = copyBtn ? copyBtn.textContent : "";
  let copiedTimer = 0;

  if (copyBtn) {
    copyBtn.hidden = false;
  }
  // PC で Web Share が使えるときだけ「その他のアプリ」（SP は summary のタップで OS シートを直接開く）。
  if (nativeBtn && prefersPanel() && canWebShare(url)) {
    nativeBtn.hidden = false;
  }

  summary.addEventListener(
    "click",
    (event) => {
      if (el.classList.contains("is-busy")) {
        event.preventDefault();
        return;
      }
      if (!el.open && !prefersPanel() && canWebShare(url)) {
        event.preventDefault();
        nativeShare(el, url, title);
      }
    },
    { signal }
  );

  el.addEventListener(
    "toggle",
    () => {
      el.classList.toggle("is-open", el.open);
      if (el.open) {
        closeOthers(el);
        const first = copyBtn && !copyBtn.hidden ? copyBtn : input;
        if (first && typeof first.focus === "function") {
          first.focus({ preventScroll: true });
        }
      } else {
        el.classList.remove("is-copied");
        if (note) {
          note.hidden = true;
        }
      }
    },
    { signal }
  );

  if (copyBtn) {
    copyBtn.addEventListener(
      "click",
      async () => {
        const ok = await copyText(url);
        if (ok) {
          window.clearTimeout(copiedTimer);
          const done = msg("shareCopied", "リンクをコピーしました");
          el.classList.add("is-copied");
          copyBtn.textContent = done;
          if (note) {
            note.hidden = true;
          }
          announce(done);
          track(el, "copy");
          copiedTimer = window.setTimeout(() => {
            el.classList.remove("is-copied");
            copyBtn.textContent = copyLabel;
          }, COPIED_MS);
          return;
        }
        // コピーできない環境: URL 欄を選択して、手動でのコピーを案内する（計測しない）。
        if (input) {
          input.focus();
          input.select();
        }
        if (note) {
          note.hidden = false;
        }
      },
      { signal }
    );
  }

  el.querySelectorAll(".js-sp-share-link").forEach((link) => {
    link.addEventListener(
      "click",
      () => {
        // 遷移は止めない（計測の失敗は push() が握りつぶす）。
        track(el, link.getAttribute("data-sp-share-method") || "");
      },
      { signal }
    );
  });

  if (nativeBtn) {
    nativeBtn.addEventListener("click", () => nativeShare(el, url, title), { signal });
  }

  if (input) {
    input.addEventListener("focus", () => input.select(), { signal });
  }
}

/**
 * 受け手向け案内（parts/share-landing-note.php）の「閉じる」。保存はしない（再読み込みで戻る）。
 *
 * @param {ParentNode} root
 * @param {AbortSignal} signal
 */
function setupLanding(root, signal) {
  root.querySelectorAll(".js-sp-share-landing").forEach((block) => {
    const close = block.querySelector(".js-sp-share-landing-close");
    if (!close) {
      return;
    }
    close.hidden = false;
    close.addEventListener(
      "click",
      () => {
        block.classList.add("is-closed");
        const main = document.getElementById("main");
        if (main) {
          main.focus({ preventScroll: true });
        }
      },
      { signal }
    );
  });
}

/**
 * 初期化。共有部品が無ければ何もしない。
 *
 * @param {ParentNode} [root]
 * @returns {AbortController|null} リスナーをまとめて外すためのコントローラ
 */
export function initShare(root = document) {
  const nodes = [...root.querySelectorAll(".js-sp-share")].filter((el) => el instanceof HTMLDetailsElement);
  const landing = root.querySelector(".js-sp-share-landing");
  if (!nodes.length && !landing) {
    return null;
  }
  const controller = new AbortController();
  const { signal } = controller;
  nodes.forEach((el) => setup(el, signal));
  setupLanding(root, signal);

  if (nodes.length) {
    // パネルの外側のクリックで閉じる。
    document.addEventListener(
      "click",
      (event) => {
        document.querySelectorAll(".js-sp-share[open]").forEach((el) => {
          if (!el.contains(event.target)) {
            closePanel(el);
          }
        });
      },
      { signal }
    );
    // Escape で閉じ、フォーカスを summary に戻す。
    document.addEventListener(
      "keydown",
      (event) => {
        if (event.key !== "Escape") {
          return;
        }
        document.querySelectorAll(".js-sp-share[open]").forEach((el) => {
          // パネルを閉じた Escape は全画面の解除（live-fullscreen.js）に使わせない。
          event.preventDefault();
          el.open = false;
          const summary = el.querySelector("summary");
          if (summary) {
            summary.focus();
          }
        });
      },
      { signal }
    );
  }
  return controller;
}
